import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const SLIDES = [
  {
    image: "/images/worship-service.jpg",
    eyebrow: "Welcome Home",
    title: ["Out of", "Complacency.", "Into Your", "Destiny."],
    body: "The Push Center is more than a Sunday morning gathering — we're a movement of people who refuse to settle.",
    primaryCta: { label: "Plan Your Visit", to: "/contact" },
    secondaryCta: { label: "Watch Latest Sermon", to: "/sermons" },
  },
  {
    image: "/images/pulpit-banner.jpg",
    eyebrow: "Sundays · 9:00 AM",
    title: ["Worship that", "moves heaven."],
    body: "Powerful worship. Practical teaching. A welcoming, spirit-filled atmosphere where you belong.",
    primaryCta: { label: "Plan Your Visit", to: "/contact" },
    secondaryCta: { label: "Service Times", to: "/about" },
  },
  {
    image: "/images/worship-team.jpg",
    eyebrow: "Find Your Place",
    title: ["A family", "on fire."],
    body: "From hospitality to worship to youth — there's a ministry where your gifts come alive.",
    primaryCta: { label: "Get Involved", to: "/ministries" },
    secondaryCta: { label: "About Us", to: "/about" },
  },
];

export default function RotatingHero() {
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % SLIDES.length), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    const id = setInterval(next, 7000);
    return () => clearInterval(id);
  }, [next]);

  const slide = SLIDES[index];

  return (
    <section className="relative min-h-[92vh] overflow-hidden flex items-center bg-black" data-testid="hero-rotator">
      {/* BG IMAGE (cross-fade) */}
      <AnimatePresence mode="sync">
        <motion.div
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2 }}
          className="absolute inset-0"
        >
          <img src={slide.image} alt="" className="w-full h-full object-cover opacity-65 bg-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/20" />
          <div className="absolute inset-0 lightning-grid opacity-40" />
        </motion.div>
      </AnimatePresence>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 items-center w-full">
        <div className="lg:col-span-7 xl:col-span-7">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <span className="w-12 h-px bg-electric" />
                <span className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold" data-testid="hero-eyebrow">
                  {slide.eyebrow}
                </span>
              </div>

              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.92] tracking-tighter">
                {slide.title.map((line, i) =>
                  i % 2 === 1 ? (
                    <span key={i} className="italic text-electric text-glow">
                      {line}
                      {i < slide.title.length - 1 && <br />}
                    </span>
                  ) : (
                    <span key={i}>
                      {line}{" "}
                    </span>
                  )
                )}
              </h1>

              <p className="mt-8 text-lg text-zinc-300 max-w-xl leading-relaxed">{slide.body}</p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to={slide.primaryCta.to}
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-2 bg-electric text-black px-7 py-4 rounded-full font-bold tracking-wide hover:bg-[#4ade80] hover:-translate-y-1 transition-all duration-300 glow-electric"
                >
                  {slide.primaryCta.label}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to={slide.secondaryCta.to}
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-4 rounded-full font-bold tracking-wide hover:border-electric hover:text-electric transition-all duration-300"
                >
                  {slide.secondaryCta.label}
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* LOGO — desktop only, hides on tablet/mobile to give the hero text breathing room */}
        <div className="hidden xl:flex lg:col-span-5 justify-end">
          <div className="relative">
            <div className="absolute -inset-10 rounded-full bg-electric/20 blur-3xl animate-pulse-glow" />
            <img
              src="/logo.png"
              alt="The Push Center Logo"
              className="relative w-72 h-72 lg:w-96 lg:h-96 object-contain lightning-flicker"
              data-testid="hero-logo"
            />
          </div>
        </div>
      </div>

      {/* CONTROLS */}
      <div className="absolute bottom-8 left-0 right-0 flex items-center justify-between px-5 lg:px-10 max-w-7xl mx-auto">
        <div className="flex gap-2" role="tablist" aria-label="Hero slides">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-selected={index === i}
              data-testid={`hero-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                index === i ? "w-10 bg-electric" : "w-5 bg-white/20 hover:bg-white/40"
              }`}
            />
          ))}
        </div>
        <div className="hidden sm:flex gap-2">
          <button
            onClick={prev}
            aria-label="Previous slide"
            data-testid="hero-prev"
            className="w-10 h-10 rounded-full border border-white/15 text-zinc-300 hover:text-electric hover:border-electric flex items-center justify-center transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={next}
            aria-label="Next slide"
            data-testid="hero-next"
            className="w-10 h-10 rounded-full border border-white/15 text-zinc-300 hover:text-electric hover:border-electric flex items-center justify-center transition-all"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
